import React, { useEffect, useMemo, useState } from 'react';
import {
    Alert, Box, Button, CircularProgress, Dialog, DialogActions, DialogContent, DialogTitle, LinearProgress,
    List, ListItemButton, ListItemText, Typography,
} from '@mui/material';
import { fileKeyOf, TRASH_NAME } from './disbox-file-manager';
import { formatSize } from './file-utils';

function relativeTime(timestamp) {
    const minutes = Math.round((Date.now() - timestamp) / 60000);
    if (minutes < 1) return 'baru saja';
    if (minutes < 60) return `${minutes} menit lalu`;
    const hours = Math.round(minutes / 60);
    if (hours < 48) return `${hours} jam lalu`;
    return `${Math.round(hours / 24)} hari lalu`;
}

function collectFiles(tree) {
    const files = new Map();
    let bytes = 0;
    const walk = (node, nodePath) => {
        for (const name of Object.keys(node.children || {})) {
            if (nodePath === '' && name === TRASH_NAME) continue;
            const child = node.children[name];
            const childPath = `${nodePath}/${name}`;
            if (child.type === 'directory') walk(child, childPath);
            else {
                const key = fileKeyOf(child);
                if (key) files.set(key, childPath);
                bytes += Number(child.size) || 0;
            }
        }
    };
    walk(tree, '');
    return { files, bytes };
}

function collectAllKeys(tree) {
    const keys = new Set();
    const walk = (node) => {
        for (const name of Object.keys(node.children || {})) {
            const child = node.children[name];
            if (child.type === 'directory') walk(child);
            else {
                const key = fileKeyOf(child);
                if (key) keys.add(key);
            }
        }
    };
    walk(tree);
    return keys;
}

// "Riwayat versi": bot mengingat versi-versi index sebelumnya. Dari sini
// bisa melihat isi versi lama, memulihkan HANYA file yang hilang, atau
// mengembalikan seluruh daftar file ke versi itu.
export default function TimeMachineDialog({ open, fileManager, onClose, onRestored }) {
    const [history, setHistory] = useState(null);
    const [error, setError] = useState(null);
    const [selected, setSelected] = useState(null);
    const [version, setVersion] = useState(null);
    const [loadingVersion, setLoadingVersion] = useState(false);
    const [busy, setBusy] = useState(null);
    const [result, setResult] = useState(null);

    useEffect(() => {
        if (!open) return;
        setHistory(null);
        setError(null);
        setSelected(null);
        setVersion(null);
        setResult(null);
        fileManager.getVersionHistory().then(setHistory).catch((e) => setError(e.message));
    }, [open, fileManager]);

    const comparison = useMemo(() => {
        if (!version) return null;
        const old = collectFiles(version.tree);
        const current = collectAllKeys(fileManager.fileTree);
        const currentVisible = collectFiles(fileManager.fileTree);
        const missing = [...old.files.entries()].filter(([key]) => !current.has(key)).map(([, path]) => path);
        const newer = [...currentVisible.files.keys()].filter((key) => !old.files.has(key)).length;
        return { fileCount: old.files.size, bytes: old.bytes, missing, newer };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [version, fileManager, result]);

    const onSelect = async (entry) => {
        setSelected(entry.messageId);
        setVersion(null);
        setResult(null);
        setLoadingVersion(true);
        try {
            setVersion(await fileManager.getVersion(entry.messageId));
        } catch (e) {
            setError(e.message);
        } finally {
            setLoadingVersion(false);
        }
    };

    const onRestoreMissing = async () => {
        setBusy({ label: 'Memeriksa & memulihkan file...', value: 0 });
        try {
            const outcome = await fileManager.restoreMissingFiles(version.tree, (done, total) => setBusy({ label: `Memeriksa ${done}/${total} file...`, value: total ? (done / total) * 100 : 0 }));
            setResult(`${outcome.restored} file dipulihkan ke lokasi aslinya.${outcome.skipped ? ` ${outcome.skipped} dilewati karena isinya sudah terhapus permanen dari Discord.` : ''}`);
            if (outcome.restored > 0) onRestored();
        } catch (e) {
            setError(e.message);
        } finally {
            setBusy(null);
        }
    };

    const onRestoreWhole = () => {
        if (!window.confirm('Kembalikan SELURUH daftar file ke versi ini? File yang ditambahkan setelah versi ini tidak tampil lagi di daftar (isinya tetap ada di Discord dan bisa dipulihkan lagi dari riwayat).')) return;
        fileManager.restoreWholeVersion(version.tree);
        setResult('Daftar file sudah dikembalikan ke versi ini.');
        onRestored();
    };

    return (
        <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
            <DialogTitle>Riwayat versi</DialogTitle>
            <DialogContent sx={{ display: 'flex', gap: 2, flexDirection: { xs: 'column', sm: 'row' }, minHeight: 320 }}>
                <Box sx={{ flex: 1, minWidth: 0, maxHeight: { xs: 240, sm: 460 }, overflow: 'auto' }}>
                    {!history && !error && <Box sx={{ py: 4, textAlign: 'center' }}><CircularProgress size={28} /></Box>}
                    {history && history.length === 0 && <Typography variant="body2" color="text.secondary">Belum ada riwayat.</Typography>}
                    {history && (
                        <List dense>
                            {history.map((entry, i) => (
                                <ListItemButton key={entry.messageId} selected={selected === entry.messageId} onClick={() => onSelect(entry)}>
                                    <ListItemText
                                        primary={new Date(entry.timestamp).toLocaleString('id-ID')}
                                        secondary={`${relativeTime(entry.timestamp)}${i === 0 ? ' · terbaru' : ''}`}
                                    />
                                </ListItemButton>
                            ))}
                        </List>
                    )}
                </Box>
                <Box sx={{ flex: 1.3, minWidth: 0 }}>
                    {!selected && (
                        <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                            Pilih salah satu versi di kiri. Setiap perubahan (upload, hapus, rename, pindah) membuat versi baru.
                            Berguna kalau ada file yang tiba-tiba hilang dari daftar.
                        </Typography>
                    )}
                    {loadingVersion && <Box sx={{ py: 4, textAlign: 'center' }}><CircularProgress size={28} /></Box>}
                    {comparison && (
                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                            <Typography variant="body2">
                                Versi ini berisi <b>{comparison.fileCount}</b> file ({formatSize(comparison.bytes)}).
                            </Typography>
                            <Typography variant="body2">
                                {comparison.missing.length > 0
                                    ? <><b>{comparison.missing.length}</b> file di versi ini tidak ada lagi di daftar sekarang:</>
                                    : 'Semua file di versi ini masih ada di daftar sekarang.'}
                            </Typography>
                            {comparison.missing.length > 0 && (
                                <Box component="ul" sx={{ m: 0, pl: 2.5, maxHeight: 160, overflow: 'auto', fontSize: 13 }}>
                                    {comparison.missing.slice(0, 100).map((p) => <li key={p} style={{ wordBreak: 'break-all' }}>{p}</li>)}
                                    {comparison.missing.length > 100 && <li>...dan {comparison.missing.length - 100} lainnya</li>}
                                </Box>
                            )}
                            {comparison.newer > 0 && (
                                <Typography variant="caption" color="text.secondary">{comparison.newer} file di daftar sekarang belum ada di versi ini.</Typography>
                            )}
                            {busy && (
                                <Box>
                                    <Typography variant="caption">{busy.label}</Typography>
                                    <LinearProgress variant="determinate" value={busy.value} />
                                </Box>
                            )}
                            {result && <Alert severity="success">{result}</Alert>}
                            <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                                <Button variant="contained" disabled={Boolean(busy) || comparison.missing.length === 0} onClick={onRestoreMissing}>
                                    Pulihkan file yang hilang
                                </Button>
                                <Button variant="outlined" color="warning" disabled={Boolean(busy)} onClick={onRestoreWhole}>
                                    Kembalikan seluruh versi ini
                                </Button>
                            </Box>
                        </Box>
                    )}
                    {error && <Alert severity="error" sx={{ mt: 1 }}>{error}</Alert>}
                </Box>
            </DialogContent>
            <DialogActions>
                <Button onClick={onClose}>Tutup</Button>
            </DialogActions>
        </Dialog>
    );
}
